import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  INSPECTION_STATUS_LABELS,
  type InspectionStatus,
  type InspectionStatusCounts,
  type OperationsReviewStatus,
} from '@/services/inspection.service';
import { useAuthStore } from '@/stores/auth.store';
import { SearchBar } from '@/components/shared/SearchBar';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

type Tab = 'assigned' | 'in_progress' | 'transfer_pending' | 'returned' | 'completed';

const TABS: {
  id: Tab;
  label: string;
  status: InspectionStatus;
  opsReviewStatus?: OperationsReviewStatus;
  /** Excluded review status — makes "In Progress" disjoint from "Returned". */
  excludeOpsReviewStatus?: OperationsReviewStatus;
  /** Which `InspectionStatusCounts` field this tab's badge reads. */
  countKey: keyof InspectionStatusCounts;
  description: string;
}[] = [
  {
    id: 'assigned',
    label: 'Assigned',
    status: 'ASSIGNED',
    countKey: 'assigned',
    description: 'Inspections assigned to you, not yet started.',
  },
  {
    // A returned inspection is IN_PROGRESS with operationsReviewStatus=RETURNED,
    // so it would otherwise appear under BOTH this tab and "Returned", and the
    // two counts would double-count it. The exclusion is applied to the list as
    // well as the count so the badge always matches what the tab shows.
    id: 'in_progress',
    label: 'In Progress',
    status: 'IN_PROGRESS',
    excludeOpsReviewStatus: 'RETURNED',
    countKey: 'inProgress',
    description: 'Inspections you are currently working on.',
  },
  {
    id: 'transfer_pending',
    label: 'Transfer Pending',
    status: 'TRANSFER_REQUESTED',
    countKey: 'transferPending',
    description: 'Inspections where you have initiated a transfer.',
  },
  {
    id: 'returned',
    label: 'Returned',
    status: 'IN_PROGRESS',
    opsReviewStatus: 'RETURNED',
    countKey: 'returned',
    description: 'Inspections returned by operations for revision.',
  },
  {
    id: 'completed',
    label: 'Completed',
    status: 'COMPLETED',
    countKey: 'completed',
    description: 'Inspections you have submitted.',
  },
];

const STATUS_BADGE: Record<string, string> = {
  ASSIGNED: 'bg-blue-500/10 text-blue-700',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  TRANSFER_REQUESTED: 'bg-orange-500/10 text-orange-700',
  COMPLETED: 'bg-teal-500/10 text-teal-700',
};

const TAB_IDS = TABS.map((t) => t.id);
const isTab = (v: string | null): v is Tab => v !== null && (TAB_IDS as string[]).includes(v);

export function MyInspectionsPage() {
  const userId = useAuthStore((s) => s.user?.userId ?? '');
  const navigate = useNavigate();
  // `?tab=` makes each tab deep-linkable so a dashboard KPI can land the
  // operator on exactly the list its number counted. An unknown or absent value
  // falls back to the previous default rather than erroring.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(isTab(tabParam) ? tabParam : 'assigned');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);

  // Keep state in step with back/forward navigation and with a second KPI click
  // into a different tab while already on this page.
  useEffect(() => {
    if (isTab(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
      setPage(1);
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const currentTab = TABS.find((t) => t.id === activeTab)!;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['my-inspections', activeTab, page, debouncedSearch, userId],
    queryFn: () =>
      inspectionService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: currentTab.status,
        ...(currentTab.opsReviewStatus
          ? { operationsReviewStatus: currentTab.opsReviewStatus }
          : {}),
        ...(currentTab.excludeOpsReviewStatus
          ? { excludeOperationsReviewStatus: currentTab.excludeOpsReviewStatus }
          : {}),
        assignedEngineerId: userId,
      }),
    enabled: !!userId,
  });

  // One request for all five tab totals. Deliberately NOT derived from the list
  // response: that reports `total` for the selected filter only, and the list is
  // paginated, so neither the page rows nor `total` can populate the other tabs.
  // Shares the list's query lifecycle, so it refetches when the data changes.
  const { data: counts } = useQuery({
    queryKey: ['my-inspections-status-counts', userId],
    queryFn: () => inspectionService.statusCounts({ assignedEngineerId: userId }),
    enabled: !!userId,
  });

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setPage(1);
    setSearch('');
    // `replace` so tab switching does not bury the previous page in history.
    setSearchParams(tab === 'assigned' ? {} : { tab }, { replace: true });
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">My Inspections</h1>
        <p className="mt-1 text-sm text-muted-foreground">All inspections assigned to you.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((tab) => {
          // Zero renders as no badge at all, never "(0)". `undefined` while the
          // counts request is in flight also renders nothing, so the label never
          // flickers through a misleading "(0)".
          const count = counts?.[tab.countKey];
          const showCount = typeof count === 'number' && count > 0;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {showCount && (
                // A real space, not just a CSS margin, so the rendered text is
                // literally "Assigned (10)" — margins do not survive into
                // textContent, copy/paste, or text-based assertions.
                //
                // Inside the button so the clickable target and focus ring are
                // unchanged. aria-hidden because the count is decorative for
                // screen readers — the accessible name stays the plain label.
                <>
                  {' '}
                  <span aria-hidden="true" className="text-xs font-normal opacity-70">
                    ({count})
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>

      <SearchBar
        value={search}
        onChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        placeholder="Search by inspection number, equipment, or customer…"
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          Loading…
        </div>
      ) : data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Inspection #</th>
                  <th className="px-4 py-3 font-medium text-foreground">Equipment</th>
                  <th className="px-4 py-3 font-medium text-foreground">Customer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground">Priority</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((insp) => (
                  <tr
                    key={insp.id}
                    onClick={() => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', insp.id))}
                    className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-xs font-medium text-foreground">
                      {insp.inspectionNumber}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{insp.equipmentName}</div>
                      <div className="text-xs text-muted-foreground">
                        {insp.equipmentAssetNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {insp.customerOrganizationName ?? insp.customerName ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[insp.status] ?? 'bg-muted text-muted-foreground'}`}
                      >
                        {INSPECTION_STATUS_LABELS[insp.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{insp.priorityName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : isError ? (
        <QueryErrorState subject="your inspections" onRetry={() => void refetch()} />
      ) : (
        <EmptyState
          icon={faClipboardList}
          title={
            search
              ? 'No inspections match your search'
              : `No ${currentTab.label.toLowerCase()} inspections`
          }
          description={search ? 'Try a different search term.' : currentTab.description}
        />
      )}
    </div>
  );
}
